import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AuthService } from '@shared/services/auth.service';
import { ToastrService } from '@shared/services/toastr.service';
import { debounceTime, fromEvent } from 'rxjs';
import {
  OrganizationCluster,
  OrganizationClusterService,
  ReceivedClusterDelegation,
} from '@shared/services/organization-cluster.service';
import { OrganizationSiteShareService, ReceivedSiteShare } from '@shared/services/organization-site-share.service';
import { SpringPage } from '@shared/page';
import { PageParams } from '@shared/services/logs/logs.service';
import { ConfirmService } from '@shared/services/confirm.service';
import { FormsModule } from '@angular/forms';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import { TypedTemplateDirective } from '@shared/directives/typed-template.directive';
import { BadgeOrganizationComponent } from '@shared/components/badge-organization/badge-organization.component';
import { LabelShareRoleComponent } from '@shared/components/label-share-role/label-share-role.component';
import { LabelDelegationRoleComponent } from '@shared/components/label-delegation-role/label-delegation-role.component';
import { LowerCasePipe } from '@angular/common';
import * as pager from '@shared/pager';

@Component({
  selector: 'app-received-shares',
  templateUrl: './received-shares.component.html',
  styleUrls: ['./received-shares.component.scss'],
  imports: [
    FormsModule,
    NgxDatatableModule,
    TypedTemplateDirective,
    BadgeOrganizationComponent,
    LabelShareRoleComponent,
    LabelDelegationRoleComponent,
    LowerCasePipe,
    TranslatePipe,
  ],
})
export class ReceivedSharesComponent implements OnInit {
  lang: string;

  @ViewChild('search') search: ElementRef;
  searchTerm: string = '';

  shares: SpringPage<ReceivedSiteShare> & PageParams = { content: [], page: 0, size: 10, totalElements: 0 };
  delegations: SpringPage<ReceivedClusterDelegation> & PageParams = { content: [], page: 0, size: 10, totalElements: 0 };

  siteShareRowType = { row: {} as ReceivedSiteShare };
  delegationRowType = { row: {} as OrganizationCluster };

  constructor(
    private auth: AuthService,
    private organizationClusterService: OrganizationClusterService,
    private organizationSiteShareService: OrganizationSiteShareService,
    public translate: TranslateService,
    private toastr: ToastrService,
    private confirmService: ConfirmService,
  ) {
    this.lang = this.auth.getCurrentLanguage();
  }

  ngOnInit() {
    this.getReceivedShares(this.shares.page);
    this.getReceivedDelegations(this.delegations.page);
  }

  ngAfterViewInit() {
    fromEvent(this.search.nativeElement, 'keyup')
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.getReceivedShares(this.shares.page);
      });
  }

  getReceivedShares(page: number) {
    this.organizationSiteShareService
      .getReceivedSiteShares({ filter: this.searchTerm }, { page, size: this.shares.size })
      .subscribe((res) => {
        this.shares.content = res.content;
        this.shares.totalElements = res.totalElements;
      });
  }

  async getReceivedDelegations(page: number) {
    this.organizationClusterService.getReceivedDelegations({ page, size: this.delegations.size }).subscribe((res) => {
      this.delegations.content = res.content;
      this.delegations.totalElements = res.totalElements;
    });
  }

  changeSharesPage(e) {
    this.shares.page = e.page - 1;
    this.getReceivedShares(this.shares.page);
  }

  changeDelegationsPage(e) {
    this.delegations.page = e.page - 1;
    this.getReceivedDelegations(this.delegations.page);
  }

  showModalDeleteShare(item: ReceivedSiteShare) {
    return this.confirmService.confirm(
      { html: this.translate.instant('DeleteShare', { item: item.site.domainName }) },
      () => this.deleteShare(item),
    );
  }

  async deleteShare(item) {
    this.organizationSiteShareService.deleteReceivedSiteShare(item.site.name)
      .subscribe(() => {
        this.getReceivedShares(this.shares.page);
        this.toastr.success();
      });
  }

  showModalDeleteDelegation(organizationCluster: OrganizationCluster) {
    return this.confirmService.confirm(
      { html: this.translate.instant('DeleteDelegation', { item: organizationCluster.cluster.name }) },
      () => this.deleteDelegation(organizationCluster),
    );
  }

  deleteDelegation(organizationCluster: OrganizationCluster) {
    this.organizationClusterService.deleteReceivedDelegations(organizationCluster.cluster.uid).subscribe((res) => {
      this.getReceivedDelegations(this.delegations.page);
      this.organizationClusterService
        .getClusters()
        .subscribe((clusters) => (this.auth.currentOrganization.clusters = clusters));
      this.toastr.success();
    });
  }

  protected readonly pager = pager;
}
